
import * as angular from "angular";
import * as moment from "moment";
import { DriverDecisionSupportType } from "../enum/DriverDecisionSupportType";
import { IDriver } from "@interfaces/IDriver";
import { DriverService } from "./driver.service";
import { DriverAssignmentService } from "./driverAssignment.service";
import { TranslationService } from "@services/translation.service";
import { DateService, DateUnit } from "./date.service";

export interface ITachoModePeriod {
    Mode: DriverTachoMode;
    ModeEnumString: string;

    ModeString: string;
    StartTime: moment.Moment;
    EndTime: moment.Moment;

    DurationString?: string;
    DurationMins?: number;

    DurationPercentage?: number;

    StringDescription?: string;
}

export enum DriverTachoMode {
    Break, // Break within work hours
    POA, // Available
    Work, // Other work i.e. not driving
    Drive, // Driving

    //Rest // Calculated
    Future = 998,
    Unknown = 999
}

export enum DriverInfringementStatus {
    Ok,
    Prewarning,
    Warning
}

export interface IDriverTachoDisplay {
    status: DriverInfringementStatus;
    statusClass: string;
    statusMessage: string;
    modeMessage: string;
    sourceMessage: string;
}

export interface IDriverTachoHours {

    ContinuousDrive?: number; // continuous drive of a driver since last break
    DailyDrive?: number;
    WeeklyDrive?: number;
    TwoWeekDrive?: number;

    MaximumDailyDrive?: number;
    MaximumWeeklyDrive?: number;
    RemainingDrive?: number;

    CardNumber?: string;

    CumulativeBreak?: number;
    BreakRestTaken?: number;

    DriverId?: number;
    DriverTachoDisplay?: IDriverTachoDisplay;
    Duration?: number;

    Id?: number;

    Mode?: DriverTachoMode;
    Packet?: string;

    TimeRelatedStates?: DriverInfringementStatus;

    EventTime?: string;

    //Name?: string;
    Driver?: IDriver;

    DriverDecisionSupportType?: DriverDecisionSupportType;
}

export interface ITachoDay {
    date: Date;
    periods: ITachoModePeriod[];
    rawData: IDriverTachoHours[];

    modeSummary: ITachoModePeriod[];
}

export interface ITachoModeSummary {
    Label: string;
    Mode: DriverTachoMode;
    ModeEnumString: string;

    DurationMins: number;
    DurationString: string;
}

export class TachoService {
    tachoStatusMessages: [string, DriverInfringementStatus][];

    static $inject = ["$q", "$http", "dateService", "driverService", "driverAssignmentService", "translationService"];

    constructor(
        private readonly $q: angular.IQService,
        private readonly $http: angular.IHttpService,
        private readonly dateService: DateService,
        private readonly driverService: DriverService,
        private readonly driverAssignmentService: DriverAssignmentService,
        private readonly translationService: TranslationService) {

        this.createStatusMessages();
    }

    getCurrentTachoForAllDrivers(mapDriversAndVehicles: boolean): angular.IPromise<IDriverTachoHours[]> {

        const driverPromise = mapDriversAndVehicles ? this.driverAssignmentService.getDriversWithVehicles() : this.$q.when([]);
        const tachoPromise = this.driverService.getDriversTacho();

        return this.$q
            .all([driverPromise, tachoPromise])
            .then(([driverList, driverTachoList]) => {

                this.mapDriversToTachoObjects(driverTachoList, driverList);

                driverTachoList.forEach(driverTacho => {
                    this.processDriverTachoDisplay(driverTacho);
                });

                return driverTachoList;
            });
    }

    getTachoDaysForDriver(driverId: number, startDate: Date, endDate: Date): angular.IPromise<ITachoDay[]> {
        startDate = this.dateService.getStartOfDay(startDate);
        endDate = this.dateService.getStartOfDay(endDate);

        var daysDiff = Math.ceil(this.dateService.getDiff(startDate, endDate, DateUnit.Day));

        return this.getHistoricalTachoForDriver(driverId, startDate, endDate).then(tachoData => {
            const tachoDays: ITachoDay[] = [];

            for (var i = 0; i < daysDiff; i++) {
                var periodStart = this.dateService.getStartOfDay(this.dateService.addOrSubtractTime(startDate, i, DateUnit.Day));

                var periodEnd = this.dateService.getEndOfDay(periodStart);

                var modePeriods = this.calculateTachoModePeriods(tachoData.filter(d => this.dateService.dateIsBetweenDates(d.EventTime, periodStart, periodEnd, true, true)));

                var tachoDay: ITachoDay = {
                    date: periodStart,
                    periods: modePeriods,
                    rawData: tachoData,
                    modeSummary: this.calculateModeSummary(modePeriods),
                };

                tachoDays.push(tachoDay);
            }

            return tachoDays;
        });
    }

    private dateIsToday(date: Date): boolean {
        const today = new Date();
        return (
            date.getFullYear() === today.getFullYear() &&
            date.getMonth() === today.getMonth() &&
            date.getDate() === today.getDate()
        );
    }

    private createModeSummary(mode: DriverTachoMode): ITachoModeSummary {

        var label = this.getModeMessage(mode);

        if (mode == 1000) { label = this.translationService.t('tachoService.TotalWork'); }

        return {
            Mode: mode,
            Label: label,
            ModeEnumString: DriverTachoMode[mode],

            DurationMins: 0,
            DurationString: "N/A",
        }
    }

    createModeSummaryMins(tachoDays: ITachoDay[]): ITachoModeSummary[] {
        const errorMinuteValue = 65535;

        var modes = [DriverTachoMode.Break, DriverTachoMode.Work, DriverTachoMode.POA, DriverTachoMode.Drive, DriverTachoMode.Future, DriverTachoMode.Unknown, 1000];

        var modeSummaryMins: ITachoModeSummary[] = [];
        modes.forEach(mode => {
            modeSummaryMins[mode] = this.createModeSummary(mode);
        });

        var tachoHours = tachoDays.reduce((current, d) => current.concat(d.periods), []) as ITachoModePeriod[];

        tachoHours.forEach(tachoHour => {
            modeSummaryMins[tachoHour.Mode].DurationMins += tachoHour.DurationMins;
        });

        modeSummaryMins[1000].DurationMins = modeSummaryMins[DriverTachoMode.Work].DurationMins + modeSummaryMins[DriverTachoMode.Drive].DurationMins;

        modeSummaryMins.forEach(summary => {
            const d = moment.duration(summary.DurationMins, "m");

            summary.DurationString = (d as any).format("h [hours], m [mins]");
        });

        modeSummaryMins = modeSummaryMins.filter(n => n);

        return modeSummaryMins;
    }

    calculateTachoModePeriods(rawTachoData: IDriverTachoHours[]): ITachoModePeriod[] {

        if (!rawTachoData || rawTachoData.length == 0) {
            return [];
        }

        var previousTachoHours: IDriverTachoHours = null;

        // Pre process data, look for huge gaps and insert a -1 tacho mode
        var processedHistoricalData: IDriverTachoHours[] = [];
        rawTachoData.forEach(data => {
            // If current mode is same as previous, count towards continuous block
            // If mode changes, create new status
            // If mode lasts for more than 5 minutes assume
            if (previousTachoHours) {
                var timeGapBetweenItems = moment(data.EventTime).diff(previousTachoHours.EventTime, "m");

                //TODO: Increase GAP as this will cater for any data delays... but i think the UTC value here is received, and not the tacho event time
                if (timeGapBetweenItems > 15) {
                    previousTachoHours.Mode = DriverTachoMode.Unknown;
                }
            }

            processedHistoricalData.push(data);
            previousTachoHours = data;
        });

        previousTachoHours = null;
        var modeArray: ITachoModePeriod[] = [];
        var previousTachoModePeriod: ITachoModePeriod = {
            Mode: DriverTachoMode.Unknown,
            ModeEnumString: DriverTachoMode[DriverTachoMode.Unknown],
            ModeString: this.getModeMessage(DriverTachoMode.Unknown),
            StartTime: moment(this.dateService.getStartOfDay(rawTachoData[0].EventTime)),
            EndTime: null
        };

        processedHistoricalData.forEach(data => {
            // Mode Switch
            if (!previousTachoModePeriod || previousTachoModePeriod.Mode != data.Mode) {
                if (previousTachoModePeriod) {

                    previousTachoModePeriod.EndTime = moment(data.EventTime);

                    this.closeTachoModePeriod(previousTachoModePeriod);
                }

                var modeString = this.getModeMessage(data.Mode);

                previousTachoModePeriod = {
                    Mode: data.Mode,
                    ModeEnumString: DriverTachoMode[data.Mode],
                    ModeString: modeString,
                    StartTime: previousTachoModePeriod?.EndTime || moment(this.dateService.getStartOfDay(data.EventTime)),
                    EndTime: moment(data.EventTime)
                }

                modeArray.push(previousTachoModePeriod);
            }
            else {
                previousTachoModePeriod.EndTime = moment(data.EventTime);
            }
        });

        if (previousTachoModePeriod) {
            this.closeTachoModePeriod(previousTachoModePeriod);
        }

        this.addStartAndEndRestPeriods(modeArray, rawTachoData);

        if (modeArray.length > 0) {
            // Create percentages
            var startTime = modeArray[0].StartTime;
            var endTime = modeArray[modeArray.length - 1].EndTime;
            var totalMins = endTime.diff(startTime, "m");
            var percentagePerMin = 100 / totalMins;

            modeArray.forEach(row => {
                row.DurationPercentage = row.DurationMins * percentagePerMin;
            });
        }

        return modeArray;
    }

    private calculateModeSummary(modePeriods: ITachoModePeriod[]): ITachoModePeriod[] {
        var modeSummary: ITachoModePeriod[] = [];

        modePeriods.forEach(modePeriod => {
            var summaryPeriod = modeSummary[modePeriod.Mode];

            if (!summaryPeriod) {
                summaryPeriod = {
                    Mode: modePeriod.Mode,
                    ModeEnumString: DriverTachoMode[modePeriod.Mode],
                    ModeString: this.getModeMessage(modePeriod.Mode),
                    StartTime: null,
                    EndTime: null,

                    DurationMins: 0,
                    DurationPercentage: 0
                };

                modeSummary[modePeriod.Mode] = summaryPeriod;
            }

            summaryPeriod.DurationMins += modePeriod.DurationMins;
            summaryPeriod.DurationPercentage += modePeriod.DurationPercentage;
        });

        // Remove emptys
        modeSummary = modeSummary.filter(ms => ms);

        modeSummary.forEach(mode => {
            this.calculateTachoModePeriodDuration(mode);
        });

        return modeSummary;
    }

    addStartAndEndRestPeriods(modePeriods: ITachoModePeriod[], rawTachoData: IDriverTachoHours[]) {

        // If we dont receive data for first minute, then also insert an unknown state from midnight to the first packet
        var firstPacketTime = moment(rawTachoData[0].EventTime);
        var startOfDay = firstPacketTime.clone().startOf("d");
        var timeToFirstPacket = firstPacketTime.diff(startOfDay, "m");

        if (timeToFirstPacket > 1) {
            var startPeriod: ITachoModePeriod = {
                DurationMins: timeToFirstPacket,
                Mode: DriverTachoMode.Unknown,
                ModeEnumString: DriverTachoMode[DriverTachoMode.Unknown],
                ModeString: this.getModeMessage(DriverTachoMode.Unknown),
                StartTime: startOfDay,
                EndTime: firstPacketTime
            };

            modePeriods.unshift(startPeriod);
            this.closeTachoModePeriod(startPeriod);
        }

        // If our last packet is before the end of the day, also insert unknown state for the remaining time of day
        var lastPacketTime = moment(rawTachoData[rawTachoData.length - 1].EventTime);
        var endOfDay = firstPacketTime.clone().endOf("d");
        var timeToEndOfDay = endOfDay.diff(lastPacketTime, "m");

        if (timeToEndOfDay > 1) {
            const packetMode = this.dateIsToday(lastPacketTime.toDate()) ? DriverTachoMode.Future : DriverTachoMode.Unknown;

            var endPeriod: ITachoModePeriod = {
                DurationMins: timeToEndOfDay,
                Mode: packetMode,
                ModeEnumString: DriverTachoMode[packetMode],
                ModeString: this.getModeMessage(packetMode),
                StartTime: lastPacketTime,
                EndTime: endOfDay
            };

            modePeriods.push(endPeriod);
            this.closeTachoModePeriod(endPeriod);
        }
    }

    closeTachoModePeriod(tachoModePeriod: ITachoModePeriod) {
        this.calculateTachoModePeriodDuration(tachoModePeriod);

        tachoModePeriod.StringDescription = `
            ${tachoModePeriod.ModeString}:
            ${this.dateService.formatDate(tachoModePeriod.StartTime.toDate(), { timeOnly: true })} to
            ${this.dateService.formatDate(tachoModePeriod.EndTime.toDate(), { timeOnly: true })}`;
    }

    calculateTachoModePeriodDuration(tachoModePeriod: ITachoModePeriod) {

        if (!tachoModePeriod.DurationMins) {
            if (!tachoModePeriod.EndTime) {
                tachoModePeriod.EndTime = moment();
            }

            tachoModePeriod.DurationMins = tachoModePeriod.EndTime.diff(tachoModePeriod.StartTime, "s") / 60;
        }

        const d = moment.duration(tachoModePeriod.DurationMins, "m");
        tachoModePeriod.DurationString = (d as any).format("h[h], m[m]");
    }

    getHistoricalTachoForDriver(driverId: number, startDate: Date, endDate: Date): angular.IPromise<IDriverTachoHours[]> {
        return this
            .$http({
                url: `/api/drivers/${driverId}/tacho/history`,
                method: "GET",
                cache: true,
                params: { minsToLive: 5, startDate: this.dateService.getStartOfDay(startDate).toISOString(), endDate: this.dateService.getEndOfDay(endDate).toISOString() }
            })
            .then((response: angular.IHttpPromiseCallbackArg<IDriverTachoHours[]>) => {
                return response.data;
            });
    }

    getModeMessage(mode: DriverTachoMode): string {

        if (mode == DriverTachoMode.Unknown) {
            return this.translationService.t("tachoService.Rest");
        }

        if (mode == DriverTachoMode.Future) {
            return this.translationService.t("Common.Unknown");
        }

        if (mode === DriverTachoMode.Work) {
            return this.translationService.t("tachoService.otherWork");
        }

        if (mode === DriverTachoMode.Break) {
            return this.translationService.t("tachoService.break");
        }

        return DriverTachoMode[mode];
    }

    processDriverTachoDisplay(driverTacho: IDriverTachoHours) {
        const status = this.getStatusMessage(driverTacho.TimeRelatedStates);

        driverTacho.DriverTachoDisplay = {
            status: status[1],
            statusClass: "",
            statusMessage: status[0],
            modeMessage: this.getModeMessage(driverTacho.Mode),
            sourceMessage: this.getSourceMessage(driverTacho.DriverDecisionSupportType)
        };

        if (status[1] === DriverInfringementStatus.Prewarning) {
            driverTacho.DriverTachoDisplay.statusClass = "orange-color";
        }
        else if (status[1] === DriverInfringementStatus.Warning) {
            driverTacho.DriverTachoDisplay.statusClass = "red-color";
        }
    }

    getCurrentTachoForDriver(driverId: number): angular.IPromise<IDriverTachoHours> {
        return this.driverService.getCurrentTachoForDriver(driverId);
    }

    getModeImage(driverTachoMode: DriverTachoMode): string {
        // Status images
        // Rest,
        // Available,
        // Work,
        // Drive
        switch (driverTachoMode) {
            case DriverTachoMode.POA:
                return "/assets/images/icons/tacho/poa_new.png";

            case DriverTachoMode.Work:
                return "/assets/images/icons/tacho/other_work_new.png";

            case DriverTachoMode.Drive:
                return "/assets/images/icons/tacho/driving_mode_new.png";

            case DriverTachoMode.Break:
            case DriverTachoMode.Unknown: // Aka REST
                return "/assets/images/icons/tacho/rest_new.png";

            default:
                return "";
        }
    }

    getStatusMessage(timeRelatedStates: number) {

        return this.tachoStatusMessages[timeRelatedStates];
    }

    mapDriversToTachoObjects(driverTachoList: IDriverTachoHours[], driverList: IDriver[]) {
        driverList.forEach(driver => {
            this.mapDriverToTachoObjects(driver, driverTachoList);
        });
    }

    mapDriverToTachoObjects(driver: IDriver, driverTachoList: IDriverTachoHours[]) {
        for (let i = 0; i < driverTachoList.length; i++) {
            const driverTacho = driverTachoList[i];

            // This MAY cause a infinite loop during serialization, bear this in mind if problems occur in the future
            if (driver && driver.Id === driverTacho.DriverId) {
                driverTacho.Driver = driver;
                driver.tacho = driverTacho;

                return;
            }
        }
    }

    private createStatusMessages() {
        this.tachoStatusMessages = [/*["", 0]*/];

        this.tachoStatusMessages[0] = [this.translationService.t("tachoService.noTimeWarning"), DriverInfringementStatus.Ok];
        this.tachoStatusMessages[1] = [this.translationService.t("tachoService.continuousDrivingWarning"), DriverInfringementStatus.Prewarning];
        this.tachoStatusMessages[2] = [this.translationService.t("tachoService.continuousDrivingExceeded"), DriverInfringementStatus.Warning];
        this.tachoStatusMessages[3] = [this.translationService.t("tachoService.dailyDrivingWarning"), DriverInfringementStatus.Prewarning];
        this.tachoStatusMessages[4] = [this.translationService.t("tachoService.dailyDrivingAlert"), DriverInfringementStatus.Warning];
        this.tachoStatusMessages[5] = [this.translationService.t("tachoService.restWarning"), DriverInfringementStatus.Prewarning];
        this.tachoStatusMessages[6] = [this.translationService.t("tachoService.restAlert"), DriverInfringementStatus.Warning];
        this.tachoStatusMessages[7] = [this.translationService.t("tachoService.weeklyDrivingWarning"), DriverInfringementStatus.Prewarning];
        this.tachoStatusMessages[8] = [this.translationService.t("tachoService.weeklyDrivingAlert"), DriverInfringementStatus.Warning];
        this.tachoStatusMessages[9] = [this.translationService.t("tachoService.twoWeekWarning"), DriverInfringementStatus.Prewarning];
        this.tachoStatusMessages[10] = [this.translationService.t("tachoService.twoWeekAlert"), DriverInfringementStatus.Warning];
        this.tachoStatusMessages[11] = [this.translationService.t("tachoService.cardExpiryWarning"), DriverInfringementStatus.Warning];
        this.tachoStatusMessages[12] = [this.translationService.t("tachoService.cardDownloadError"), DriverInfringementStatus.Warning];
        this.tachoStatusMessages[13] = [this.translationService.t("tachoService.other"), DriverInfringementStatus.Warning];
    }

    private getSourceMessage(driverDecisionSupportType: DriverDecisionSupportType): string {

        switch (driverDecisionSupportType) {
            //case DriverDecisionSupportType.DigiDL:
            //    return "Tacho DDS";
            //case DriverDecisionSupportType.KLine:
            //    return "KLine - Interpreted";
            case DriverDecisionSupportType.DigiDL:
                return "DDS";

            case DriverDecisionSupportType.KLine:
                return "KLine";
        }

        return "";
    }
}
